import { useSyncExternalStore } from 'react';

/**
 * Artwork's colours, in both appearances: the Apple system roles index.css is built on,
 * with Flobi's violet as the accent.
 *
 * One source for both sides of the app: index.css declares the same values as CSS
 * variables for the interface, and the canvas renderer reads them from here, so the page
 * behind the artboards and the selection outline always match the chrome around them.
 */
export interface Palette {
  /** The infinite canvas and the gaps between panels: the quietest surface. */
  canvas: string;
  /** The app background outside a file. */
  page: string;
  /** Panels, toolbars, popovers. */
  surface: string;
  /** A panel above a panel, and inputs. */
  surfaceRaised: string;
  /** Hairlines and dividers. */
  line: string;
  /** Text and icons. */
  ink: string;
  /** Labels, units, secondary text. */
  inkMuted: string;
  /** Selection, live state, the value being read. */
  accent: string;
  accentStrong: string;
  accentInk: string;
  /** The warm grey from the reference: quiet surfaces and callouts. */
  sand: string;
  sandInk: string;
  /** The rail down the left edge, and the wells sunk into it. */
  rail: string;
  well: string;
  /** The pale pill that slides in a well. */
  wellThumb: string;
  /**
   * The colour of everything the editor draws ON the artwork: the selection frame, the
   * handles, the anchors, the lit edges.
   *
   * Deliberately not the brand accent. That one is chosen to sit beside the chrome; this
   * one has to read against whatever the person has drawn, in either theme, and be the
   * one colour on the canvas that is unmistakably the editor talking rather than the
   * drawing. It is the same in both themes for that reason.
   */
  selection: string;
}

/** What the editor draws over the artwork with, in both themes. */
const SELECTION = '#8b5cf6';

// The canvas and the few colours the renderer draws the rulers with. Everything else
// the chrome needs lives in index.css as the new system's roles; these are only what
// CanvasKit has to be handed as values.
const dark: Palette = {
  canvas: '#1e1e1e',
  page: '#171717',
  surface: '#2c2c2c',
  surfaceRaised: '#383838',
  line: '#444444',
  ink: '#ffffff',
  inkMuted: '#c0c0c0',
  accent: '#b28aff',
  accentStrong: '#c4a4ff',
  accentInk: '#1b102f',
  sand: '#b4b2a4',
  sandInk: '#111111',
  rail: '#2c2c2c',
  well: '#1a1a1a',
  wellThumb: '#f4f4f4',
  selection: SELECTION,
};

const light: Palette = {
  canvas: '#e9e9e9',
  page: '#f5f5f5',
  surface: '#ffffff',
  surfaceRaised: '#f2f2f2',
  line: '#e1e1e1',
  ink: '#242424',
  inkMuted: '#636363',
  accent: '#7c3aed',
  accentStrong: '#6d28d9',
  accentInk: '#ffffff',
  sand: '#b4b2a4',
  sandInk: '#111111',
  rail: '#ffffff',
  well: '#e5e5e5',
  wellThumb: '#ffffff',
  selection: SELECTION,
};

export const PALETTES = { dark, light } as const;

export type ThemeName = 'dark' | 'light';
/** What the person chose: a theme, or to follow the system. */
export type ThemeChoice = ThemeName | 'system';

const STORAGE_KEY = 'artwork:theme';

export function systemTheme(): ThemeName {
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function storedChoice(): ThemeChoice {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

/** Stamps the choice on the document so the CSS variables switch. */
export function applyTheme(choice: ThemeChoice): ThemeName {
  const resolved = choice === 'system' ? systemTheme() : choice;
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
  root.style.colorScheme = resolved;
  try {
    if (choice === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // A private window can refuse storage; the choice then lasts for this visit.
  }
  return resolved;
}

/**
 * One theme for the whole app. The choice lives here rather than in each component, so
 * the canvas, the chrome and Clerk's own menus all turn over together.
 */
const listeners = new Set<() => void>();
let current: ThemeChoice = 'system';
let resolvedNow: ThemeName = 'dark';
let watchingSystem: (() => void) | null = null;

function publish(choice: ThemeChoice) {
  current = choice;
  resolvedNow = applyTheme(choice);
  watchSystem();
  for (const listener of listeners) listener();
}

/** While following the system, changes there are changes here. */
function watchSystem() {
  const media = window.matchMedia?.('(prefers-color-scheme: light)');
  if (!media) return;
  if (current !== 'system') {
    if (watchingSystem) watchingSystem();
    watchingSystem = null;
    return;
  }
  if (watchingSystem) return;
  const follow = () => {
    resolvedNow = applyTheme('system');
    for (const listener of listeners) listener();
  };
  media.addEventListener('change', follow);
  watchingSystem = () => media.removeEventListener('change', follow);
}

/** Sets the theme up from storage. Call once, before the first paint. */
export function startTheme(): ThemeName {
  current = storedChoice();
  resolvedNow = applyTheme(current);
  watchSystem();
  return resolvedNow;
}

/** The current theme, the choice behind it, and a way to change it. */
export function useTheme() {
  const choice = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
    () => current,
  );
  return { theme: resolvedNow, choice, setChoice: publish, palette: PALETTES[resolvedNow] };
}

/** A hex colour as the renderer wants it: channels from 0 to 1. */
export function rgba(hex: string, alpha = 1) {
  const value = parseInt(hex.slice(1), 16);
  return { r: ((value >> 16) & 255) / 255, g: ((value >> 8) & 255) / 255, b: (value & 255) / 255, a: alpha };
}

export function hexToColor(hex: string) {
  return rgba(hex);
}

export function colorToHex(color: { r: number; g: number; b: number }): string {
  const channel = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0');
  return `#${channel(color.r)}${channel(color.g)}${channel(color.b)}`;
}
